import { ButtonGhost, ButtonNavigation, Colors, Icon } from '@amelio/ui-library';
import { useCallback } from 'react';

import { useAmelioTranslations } from '@/hooks';

export type NavigationFooterProps = {
    isLast: boolean;
    isPreview: boolean;
    onNext: () => void;
    onPrevious?: () => void;
    onSkip?: () => void;
    onSubmit?: () => void;
    isSaving?: boolean;
};

export const NavigationFooter = ({
    isLast,
    isPreview,
    onNext,
    onPrevious,
    onSkip,
    onSubmit,
    isSaving = false,
}: NavigationFooterProps) => {
    const { t } = useAmelioTranslations();

    const handleNext = useCallback(() => {
        if (isLast && onSubmit) {
            onSubmit();
            return;
        }
        onNext();
    }, [isLast, onSubmit, onNext]);

    const getNextButtonLabel = (): string => {
        if (!isLast) {
            return t('answer.nextQuestion');
        }
        if (isPreview) {
            return t('reviewAnswer.closePreview');
        }
        return t('answer.submit');
    };

    const nextButtonLabel = getNextButtonLabel();
    const nextButtonConfig = isLast
        ? {
              iconName: 'essential-send-plane' as const,
              testId: 'survey-submit-button',
              ariaLabel: t('answer.ariaSubmit'),
          }
        : {
              iconName: 'arrow-arrow-right' as const,
              testId: 'survey-next-button',
              ariaLabel: t('answer.ariaNextQuestion'),
          };

    return (
        <div
            className={
                'relative z-10 mt-6 flex flex-row items-center justify-between gap-2 md:mt-12 md:gap-6'
            }
        >
            {onPrevious ? (
                <ButtonNavigation
                    direction={'previous'}
                    size={'md'}
                    className={'text-responsive-xs-base'}
                    icon={
                        <Icon
                            name={'arrow-arrow-left-1'}
                            color={Colors.BASE}
                            size={'md'}
                            strokeWidth={3}
                        />
                    }
                    onClick={onPrevious}
                    disabled={isSaving}
                    ariaLabel={t('answer.ariaPreviousQuestion')}
                    dataTestId={'survey-previous-button'}
                >
                    {t('answer.previousQuestion')}
                </ButtonNavigation>
            ) : (
                <div />
            )}

            <div className={'flex flex-row items-center gap-2 md:gap-4'}>
                {onSkip && (
                    <ButtonGhost
                        size={'md'}
                        className={'text-responsive-xs-base font-semibold'}
                        onClick={onSkip}
                        disabled={isSaving}
                        ariaLabel={t('answer.ariaSkipQuestion')}
                        dataTestId={'survey-skip-button'}
                    >
                        {t('answer.skipThisQuestion')}
                    </ButtonGhost>
                )}

                <ButtonNavigation
                    direction={'next'}
                    size={'md'}
                    className={'text-responsive-xs-base'}
                    icon={
                        <Icon
                            name={nextButtonConfig.iconName}
                            color={Colors.BASE}
                            size={'md'}
                            strokeWidth={3}
                        />
                    }
                    onClick={handleNext}
                    disabled={isSaving}
                    dataTestId={nextButtonConfig.testId}
                    ariaLabel={nextButtonConfig.ariaLabel}
                >
                    {nextButtonLabel}
                </ButtonNavigation>
            </div>
        </div>
    );
};
